// Serviço para chamadas de API do Dashboard
import { processPredictionData, getUniqueOccurrences, filterOccurrencesByPlots } from "../utils/helpers";
import { storageService } from "./storageService";
import logger from "../../../utils/logger";
import { isMockMode, generateMockPredictions } from "../../../utils/mockData";
import { createRegistry } from "../../../services/domain/registryApiService";
import { getPrediction as getPredictionApi } from "../../../services/domain/predictionApiService";

export const dashboardService = {
  /**
   * Busca previsões da API
   * @returns {Promise<Array>} Array de dados de previsão processados
   */
  getPrediction: async () => {
    logger.info("Iniciando busca de previsões");

    // Verificar se está em modo mock
    if (isMockMode()) {
      logger.info("Modo mock ativado, gerando previsões mock");
      
      // Gerar previsões mock
      const mockData = generateMockPredictions();
      
      // Processar dados mock como se fossem da API
      let predictionData = processPredictionData(mockData);
      
      if (!Array.isArray(predictionData) || predictionData.length === 0) {
        logger.warn("Nenhum dado de previsão mock encontrado");
        return [];
      }

      // Obter ocorrências únicas
      const uniqueOccurrences = getUniqueOccurrences(predictionData);
      logger.debug("Ocorrências únicas mock encontradas", {
        uniqueCount: uniqueOccurrences.length,
        region: storageService.getRegion()
      });

      // Filtrar por plots válidos
      const plots = storageService.getPlots();
      logger.debug("Plots carregados do localStorage", {
        plotsCount: plots.length
      });

      const filteredOccurrences = filterOccurrencesByPlots(uniqueOccurrences, plots);

      logger.success("Dados mock processados com sucesso", {
        totalOccurrences: filteredOccurrences.length,
        uniqueBeforeFilter: uniqueOccurrences.length,
        filteredAfterFilter: filteredOccurrences.length
      });

      // Salvar no localStorage para uso offline
      storageService.setUniqueOccurrences(filteredOccurrences);

      return filteredOccurrences;
    }

    if (!navigator.onLine) {
      logger.info("Modo offline, usando dados do localStorage");
      const occurrences = storageService.getUniqueOccurrences();
      return occurrences;
    }

    try {
      const enterprise = localStorage.getItem("enterprise") || "ACM";
      const data = await getPredictionApi(enterprise);

      logger.debug("Resposta recebida da API de previsão", {
        message: data?.message,
        codigo: data?.codigo,
        hasDados: !!data?.dados,
        dadosType: Array.isArray(data?.dados) ? "array" : typeof data?.dados
      });

      const predictionData = processPredictionData(data);
      if (!Array.isArray(predictionData) || predictionData.length === 0) {
        logger.warn("Nenhum dado de previsão encontrado");
        return [];
      }

      const uniqueOccurrences = getUniqueOccurrences(predictionData);
      const plots = storageService.getPlots();
      const filteredOccurrences = filterOccurrencesByPlots(uniqueOccurrences, plots);

      storageService.setUniqueOccurrences(filteredOccurrences);
      return filteredOccurrences;
    } catch (error) {
      logger.error("Erro ao buscar previsões", error);

      if (error.response?.status === 401) {
        const authError = new Error("Not authenticated");
        authError.response = error.response;
        throw authError;
      }

      throw error;
    }
  },

  /**
   * Envia registros salvos offline quando volta online
   * @param {Array} registers - Array de registros para enviar
   * @returns {Promise<Object>} Resposta da API
   */
  sendOfflineRegisters: async (registers) => {
    logger.info("Enviando registros offline", {
      count: registers.length
    });

    // Verificar se está em modo mock
    if (isMockMode()) {
      logger.info("Modo mock: simulando envio de registros offline (não enviando para API)");
      // Simular delay de rede
      await new Promise(resolve => setTimeout(resolve, 500));
      logger.success("Registros mock processados (não enviados)");
      return { success: true, mock: true };
    }

    try {
      const data = await createRegistry(registers);
      logger.success("Registros enviados com sucesso");
      return data;
    } catch (error) {
      logger.error("Erro ao enviar registros offline", error);
      throw error;
    }
  }
};

